import React from 'react';
import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Pencil, X, Check } from 'lucide-react';
import { useToastNotification } from '@/contexts/ToastContext';

const SentimentScore = () => {
  const toast = useToastNotification();
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [filter, setFilter] = useState('all'); // 'all', 'contact', 'organization'
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editNote, setEditNote] = useState('');

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    // Filter data based on selected filter
    if (filter === 'all') {
      setFilteredData(data);
    } else {
      setFilteredData(data.filter(item => item.entityType === filter));
    }
  }, [filter, data]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${backendUrl}/api/sentiment-tracking`, {
        credentials: 'include',
      });
      
      if (response.ok) {
        const result = await response.json();
        setData(result);
        setFilteredData(result);
      } else {
        toast.error('Failed to load sentiment data');
      }
    } catch (error) {
      console.error('Error fetching sentiment data:', error);
      toast.error('Error loading sentiment data');
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (item) => {
    setEditingId(item.id);
    setEditNote(item.sentimentNote || '');
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditNote('');
  };

  const handleSaveNote = async (item) => {
    try {
      const response = await fetch(
        `${backendUrl}/api/sentiment-tracking/${item.entityType}/${item.id}`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({ sentiment_note: editNote }),
        }
      );

      if (response.ok) {
        // Update local data
        setData(prevData =>
          prevData.map(d =>
            d.id === item.id ? { ...d, sentimentNote: editNote } : d
          )
        );
        setEditingId(null);
        setEditNote('');
        toast.success('Sentiment note updated');
      } else {
        toast.error('Failed to update note');
      }
    } catch (error) {
      console.error('Error updating sentiment note:', error);
      toast.error('Error updating note');
    }
  };

  const handleKeyPress = (e, item) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSaveNote(item);
    } else if (e.key === 'Escape') {
      handleCancelEdit();
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3 }}
      className="space-y-6"
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-semibold text-zinc-100">
            Sentiment Tracking
          </h1>
          <p className="text-zinc-400 mt-1">
            Track sentiment notes for contacts and organizations
          </p>
        </div>

        {/* Filter Buttons */}
        <div className="flex gap-2">
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filter === 'all'
                ? 'bg-white/10 text-white'
                : 'bg-white/5 text-zinc-400 hover:bg-white/10 hover:text-white'
            }`}
          >
            All
          </button>
          <button
            onClick={() => setFilter('contact')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filter === 'contact'
                ? 'bg-white/10 text-white'
                : 'bg-white/5 text-zinc-400 hover:bg-white/10 hover:text-white'
            }`}
          >
            Contacts
          </button>
          <button
            onClick={() => setFilter('organization')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filter === 'organization'
                ? 'bg-white/10 text-white'
                : 'bg-white/5 text-zinc-400 hover:bg-white/10 hover:text-white'
            }`}
          >
            Organizations
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-[#0f1216] border border-white/10 rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-zinc-400">Loading...</div>
        ) : filteredData.length === 0 ? (
          <div className="p-12 text-center text-zinc-400">No data found</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left px-6 py-4 text-sm font-medium text-zinc-400 w-1/5">
                    Name
                  </th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-zinc-400 w-1/5">
                    Email
                  </th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-zinc-400 w-1/6">
                    Status
                  </th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-zinc-400 w-2/5">
                    Note
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredData.map((item, index) => (
                  <tr
                    key={item.id}
                    className={`border-b border-white/5 hover:bg-white/5 transition-colors ${
                      index % 2 === 0 ? 'bg-transparent' : 'bg-white/[0.02]'
                    }`}
                  >
                    <td className="px-6 py-4 text-sm text-zinc-200">
                      <div className="flex items-center gap-2">
                        {item.name}
                        <span className="text-xs text-zinc-500">
                          ({item.entityType})
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-zinc-400">
                      {item.email || '-'}
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`inline-flex px-2 py-1 rounded text-xs font-medium ${
                          item.status === 'active'
                            ? 'bg-green-500/10 text-green-400'
                            : item.status === 'inactive'
                            ? 'bg-red-500/10 text-red-400'
                            : 'bg-yellow-500/10 text-yellow-400'
                        }`}
                      >
                        {item.status}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      {editingId === item.id ? (
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            value={editNote}
                            onChange={(e) => setEditNote(e.target.value)}
                            onKeyDown={(e) => handleKeyPress(e, item)}
                            autoFocus
                            className="flex-1 bg-white/5 border border-white/10 rounded px-3 py-1.5 text-sm text-zinc-200 focus:outline-none focus:border-white/20"
                            placeholder="Enter sentiment note..."
                          />
                          <button
                            onClick={() => handleSaveNote(item)}
                            className="p-1.5 text-green-400 hover:bg-green-500/10 rounded transition-colors"
                            title="Save"
                          >
                            <Check size={16} />
                          </button>
                          <button
                            onClick={handleCancelEdit}
                            className="p-1.5 text-red-400 hover:bg-red-500/10 rounded transition-colors"
                            title="Cancel"
                          >
                            <X size={16} />
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 group">
                          <span className="text-sm text-zinc-400 flex-1">
                            {item.sentimentNote || 'No note'}
                          </span>
                          <button
                            onClick={() => handleEditClick(item)}
                            className="p-1.5 text-zinc-500 hover:text-zinc-300 opacity-0 group-hover:opacity-100 transition-all"
                            title="Edit note"
                          >
                            <Pencil size={14} />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default SentimentScore;
